import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DefaultButton } from "./DefaultButton";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Step = {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  imageAlt?: string;
};

type StepDialogMode = "dialog" | "fullscreen";

type StepDialogProps = {
  isOpen: boolean;
  steps: Step[];
  onLastAction?: () => void | Promise<void>;
  onClose?: () => void;
  lastStepLabel?: string;
  lastStepLoadingLabel?: string;
  lastStepLoading?: boolean;
  lastStepError?: string | null;
  shouldShowHeaderText?: boolean;
  preventOutsideDismiss?: boolean;
  mode?: StepDialogMode;
};

const dialogContentBaseClasses =
  "p-0! space-y-0! bg-[#1D2B53]! max-w-sm! sm:max-w-full!";

const dialogModeClasses = "absolute! overflow-hidden! rounded-xl! w-full!";

const fullscreenModeClasses =
  "absolute! inset-0! top-0! left-0! right-0! bottom-0! flex! h-full! max-h-none! w-full! max-w-none! translate-x-0! translate-y-0! flex-col! rounded-none! border-0! overflow-y-auto!";

export function StepDialog({
  isOpen,
  steps,
  onLastAction,
  onClose,
  lastStepLabel,
  lastStepLoadingLabel,
  lastStepLoading = false,
  lastStepError,
  shouldShowHeaderText = false,
  preventOutsideDismiss = false,
  mode = "dialog",
}: StepDialogProps) {
  const resolvedLastStepLabel = lastStepLabel ?? t("stepDialog.getStarted");
  const resolvedLastStepLoadingLabel =
    lastStepLoadingLabel ?? t("stepDialog.loading");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const isFullscreen = mode === "fullscreen";
  const fullscreenPortalContainer =
    typeof document !== "undefined"
      ? document.getElementById("page-content")
      : undefined;

  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0);
    }
  }, [isOpen]);

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  const handleNext = () => {
    if (isLastStep && lastStepLoading) {
      return;
    }

    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      onLastAction?.();
    }
  };

  if (steps.length === 0) return null;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        style={
          isFullscreen
            ? {
                background: "#1D2B53",
                borderImage: "none",
              }
            : {}
        }
        className={cn(
          dialogContentBaseClasses,
          isFullscreen ? fullscreenModeClasses : dialogModeClasses,
        )}
        portalContainer={fullscreenPortalContainer}
        overlayClassName={isFullscreen ? "absolute! inset-0!" : undefined}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={
          preventOutsideDismiss ? (event) => event.preventDefault() : undefined
        }
        onEscapeKeyDown={
          preventOutsideDismiss ? (event) => event.preventDefault() : undefined
        }
      >
        {currentStep.imageUrl && (
          <div
            className="relative flex justify-center"
            style={{
              backgroundImage: `url("https://cdn.navalabs.io/images/quick-tutorial/starry-night_7jZMwg.png")`,
              backgroundSize: "contain",
            }}
          >
            <img
              src={currentStep.imageUrl}
              alt={currentStep.imageAlt ?? currentStep.title}
              className={cn(
                "w-full object-contain object-bottom",
                isFullscreen ? "h-[50svh]" : "h-auto",
              )}
              loading="lazy"
            />
            {shouldShowHeaderText && (
              <h6 className="absolute -translate-y-1/2 top-6 text-base sm:text-xl text-white text-shadow-black">
                {t("stepDialog.headerText")}
              </h6>
            )}
          </div>
        )}
        <DialogHeader className="px-4 space-y-2 text-center -mt-1! sm:-mt-0.5!">
          <DialogTitle className="text-left text-xl sm:text-2xl font-bold text-white capitalize">
            {currentStep.title}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-white/70 text-left">
            {currentStep.description}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "w-full flex flex-col items-center uppercase pb-4 px-4",
            isFullscreen &&
              "mt-auto pb-[max(1rem,env(safe-area-inset-bottom))]",
          )}
        >
          {isLastStep && lastStepError && (
            <p
              className="mb-2 w-full text-left text-xs leading-snug text-red-200"
              role="alert"
            >
              {lastStepError}
            </p>
          )}
          <DefaultButton
            onClick={handleNext}
            className="w-full h-10 uppercase"
            disabled={isLastStep && lastStepLoading}
            aria-busy={isLastStep && lastStepLoading}
          >
            {isLastStep
              ? lastStepLoading
                ? resolvedLastStepLoadingLabel
                : resolvedLastStepLabel
              : t("stepDialog.next")}
          </DefaultButton>
          {/*
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <span
                key={step.id}
                className={`h-2 w-2 rounded-full transition-colors ${
                  index === currentStepIndex ? "bg-white" : "bg-white/30"
                }`}
              />
            ))}
          </div>
          */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
